import { useDashboardContext } from '@/contexts/DashboardContext.tsx';
import { SnackbarContext } from '@/contexts/SnackbarContext.tsx';
import { UserContext } from '@/contexts/UserContext.tsx';
import { getCachedCampaigns } from '@/services/AdsAccountsService';
import { updateDynamicPage } from '@/services/DynamicPagesService.ts';
import * as Sentry from '@sentry/react';
import { AdsCampaign, DynamicPageConnection } from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import { useContext, useEffect, useState } from 'react';
import facebookAdsIcon from '../../../assets/images/facebookAdsIcon.png';
import googleAdsIcon from '../../../assets/images/googleAdsIcon.png';
import { useDynamicPageSettingsContext } from './DynamicPageSettingsContext';
import { DynamicPageSettingsModalTabWrapper } from './DynamicPageSettingsModalTabWrapper';

function getConnectionTypeLabel(connection: DynamicPageConnection) {
  switch (connection.type) {
    case 'adsAccount':
      return 'Ads Account: ';

    case 'adsCampaign':
      return 'Ads Campaign: ';
    default:
      return '';
  }
}

export const AdsAccountsConnections = () => {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { adsAccounts, analyticsRange } = useDashboardContext();
  const { dynamicPageId, dynamicPage } = useDynamicPageSettingsContext();

  const [edit, setEdit] = useState<boolean>(false);
  const [adsCampaigns, setAdsCampaigns] = useState<AdsCampaign[]>();
  const [connections, setConnections] =
    useState<Record<string, DynamicPageConnection>>();

  useEffect(() => {
    if (!dynamicPage) return;

    setConnections(dynamicPage.connections);
  }, [dynamicPage]);

  useEffect(() => {
    if (!selectedAccount) {
      setAdsCampaigns(undefined);
      return;
    }

    getCachedCampaigns(selectedAccount?.id)
      .then((res) => {
        setAdsCampaigns(res);
      })
      .catch((e) => {
        Sentry.captureException(e);
        show(e.message, 'warn');
      });
  }, [selectedAccount, analyticsRange, show]);

  const onSave = async () => {
    if (!selectedAccount || !dynamicPageId) return;
    try {
      await updateDynamicPage(selectedAccount.id, dynamicPageId, {
        connections,
      });
      show('Updated');
      setEdit(false);
    } catch (_) {
      show('Failed to update');
    }
  };

  const onDiscard = () => {
    setConnections(dynamicPage?.connections);
    setEdit(false);
  };

  return (
    <DynamicPageSettingsModalTabWrapper
      edit={edit}
      onDiscard={onDiscard}
      onSave={onSave}
    >
      <div className={'space-y-2 md:col-span-2'}>
        <h2 className="text-base-700 font-inter text-sm font-medium">
          Connections
        </h2>
        <ChipsAutocomplete<DynamicPageConnection>
          loading={!adsAccounts || !adsCampaigns}
          getValue={(c) => c.name}
          renderItem={(c) => (
            <span className="inline-flex items-center gap-x-2">
              <img
                src={c.provider === 'google' ? googleAdsIcon : facebookAdsIcon}
                className="size-3"
              />
              {`${getConnectionTypeLabel(c)}${c.name}`}
            </span>
          )}
          selected={connections ? Object.values(connections ?? {}) : []}
          onChange={(selected) => {
            setConnections(
              Object.fromEntries(
                selected.map((s) => [`${s.provider}-${s.id}`, s]),
              ),
            );
            setEdit(true);
          }}
          getOptionId={(t) => t.id}
          options={[
            ...(adsCampaigns?.map(
              (c) =>
                ({
                  id: c.id,
                  name: c.name,
                  type: 'adsCampaign',
                  provider: c.provider,
                  adsAccountId: c.adsAccountId,
                }) satisfies DynamicPageConnection,
            ) ?? []),
            ...(adsAccounts?.map(
              (a) =>
                ({
                  id: a.id,
                  name: a.data().name,
                  type: 'adsAccount',
                  provider: a.data().provider,
                  adsAccountId: a.id,
                }) satisfies DynamicPageConnection,
            ) ?? []),
          ]}
          className="w-full"
        />
      </div>
    </DynamicPageSettingsModalTabWrapper>
  );
};
